import type {
  InputDescription,
  ValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import {
  Button,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateIfPresent } from "../../../../../../../../../locales/i18n";
import type { ActionCandidate } from "../../../../../../../../../types/candidate";
import { MAX_DEPTH } from "../JsonEditor";
import { KeyValue } from "../KeyValue/KeyValue";
import { PropertyModal } from "../Modal/property-modal";
import { ModalSelectField } from "../Modal/type-select-modal";
import { buttonStyles, ButtonWrapper, VerticalLine } from "../style";
import { useValueType } from "../utils/get-input-value-type.hook";
import { withInstruction } from "../with-instructions";
import * as S from "./style";

export const JsonObject = observer<{
  config?: ValueConfig;
  action?: ActionCandidate;
  description: InputDescription;
  label?: string | number;
  onClick: () => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onlyStructure?: boolean;
  depth: number;
  index: number;
  fromArray?: boolean;
}>(
  ({
    label,
    onClick,
    onlyStructure,
    depth,
    onRemove,
    onEdit,
    index,
    action,
    description,
    config,
    fromArray,
  }) => {
    const { t } = useTranslation();
    const [typeSelectModalOpen, setTypeSelectModalOpen] =
      useState<boolean>(false);
    const [propertyModalOpen, setPropertyModalOpen] = useState<boolean>(false);
    const [propToEdit, setpPropToEdit] = useState<ValueConfig_V | undefined>();
    const [editPropIndex, setEditPropIndex] = useState<number | undefined>();

    const { getInputValueTypeByValue } = useValueType();

    const addItem = (): void => {
      setpPropToEdit(undefined);
      setTypeSelectModalOpen(true);
    };
    const removeItem = (index: number): void => {
      config?.values.splice(index, 1);
      onClick();
    };

    const editItem = (index: number): void => {
      setpPropToEdit(config?.values[index]);
      setEditPropIndex(index);
      setPropertyModalOpen(true);
      onClick();
    };

    let text =
      label === undefined
        ? translateIfPresent("caption." + "JSON Object", "JSON Object")
        : `${label}${onlyStructure ? "" : ":"} `;

    if (onlyStructure) {
      const jsonLabel = withInstruction(description)
        ? t(`caption.output-values`)
        : t("caption.JSON Object");
      text = fromArray
        ? `${t("caption.List")}: ${t("caption.JSON Object")}`
        : jsonLabel;
      if (label !== undefined) {
        text = index !== undefined && !fromArray ? `${label}: ${text}` : text;
      } else {
        text =
          index !== undefined && !fromArray ? `${label ?? ""}${text}` : text;
      }
    }

    return (
      <S.Root>
        <S.Title>
          <span>
            <Icon size={"S"} name="object" />
            <Text weight="medium" color="tertiary">
              {text}
            </Text>
          </span>
          <S.RightItems>
            {depth > 1 && (
              <S.RightButton>
                <Tooltip
                  text={t("automation-form.update-response")}
                  placements={["top", "bottom"]}
                  padding={2}
                  target={
                    <IconButton
                      name="edit-thin"
                      size="S"
                      ariaLabel="Edit Button"
                      color="currentColor"
                      hoverColor="currentColor"
                      onClick={() => {
                        index !== undefined && onEdit && onEdit(index);
                      }}
                    />
                  }
                />
              </S.RightButton>
            )}
            <S.RightButton>
              <Tooltip
                text={t("automation-form.delete-response")}
                placements={["top", "bottom"]}
                padding={2}
                target={
                  <IconButton
                    name="trash-thin"
                    size="S"
                    ariaLabel="Remove Button"
                    color="currentColor"
                    hoverColor="currentColor"
                    onClick={() => onRemove(index)}
                  />
                }
              />
            </S.RightButton>
          </S.RightItems>
        </S.Title>

        <VerticalLine>
          <>
            {config?.values.map((input, index) => {
              return (
                <Fragment key={index}>
                  <KeyValue
                    description={description}
                    keyValue={
                      input.value.oneofKind === "keyValue"
                        ? input.value.keyValue
                        : undefined
                    }
                    onClick={onClick}
                    onlyStructure={onlyStructure}
                    onRemove={removeItem}
                    onEdit={editItem}
                    depth={depth + 1}
                    index={index}
                    action={action}
                  />
                  {propertyModalOpen && editPropIndex === index && (
                    <PropertyModal
                      description={description}
                      onlyStructure={onlyStructure}
                      onClose={() => setPropertyModalOpen(false)}
                      onChange={onClick}
                      excessDepth={depth > MAX_DEPTH}
                      action={action}
                      keyValueConfig={config}
                      bodyType={
                        config === undefined || editPropIndex === undefined
                          ? ""
                          : getInputValueTypeByValue(config, editPropIndex)
                      }
                      propToEdit={propToEdit}
                      editPropIndex={editPropIndex}
                    />
                  )}
                </Fragment>
              );
            })}
            {typeSelectModalOpen && (
              <ModalSelectField
                description={description}
                onlyStructure={onlyStructure}
                onClose={() => setTypeSelectModalOpen(false)}
                onChange={onClick}
                excessDepth={depth > MAX_DEPTH}
                action={action}
                keyValueConfig={config}
              />
            )}
            <ButtonWrapper>
              <Button
                iconName="plus"
                iconSize="S"
                onClick={addItem}
                styles={buttonStyles}
              >
                {withInstruction(description)
                  ? t("automation-form.add-output")
                  : t("automation-form.select-field")}
              </Button>
            </ButtonWrapper>
          </>
        </VerticalLine>
      </S.Root>
    );
  },
);
